import { Injectable, inject } from "@angular/core";
import {
  HttpErrorResponse,
  type HttpInterceptor,
  type HttpRequest,
  type HttpHandler,
  type HttpEvent,
} from "@angular/common/http";
import { EMPTY, from, throwError, type Observable } from "rxjs";
import { catchError, mergeMap } from "rxjs/operators";
import { Router } from "@angular/router";
import { MsalService } from "@azure/msal-angular";
import { InteractionRequiredAuthError } from "@azure/msal-browser";

import { ApiConfigService } from "../services/api-config.service";
import { environment } from "../../../environments/environment";
import { isOurBackendUrl } from "./is-backend-url";

/**
 * Appends an MSAL Bearer ACCESS token to every request targeting the app's
 * own backends (apiBase / navBase / docApi).
 *
 * The token is acquired silently for the API scope (environment.apiConfig.scopes,
 * e.g. `api://<client-id>/access_as_user`) — NOT the OIDC ID token. The backend
 * services validate `aud` against their configured API audience, so an access
 * token scoped to the API is required; an ID token (aud = client id) is rejected.
 *
 * `acquireTokenSilent` uses the cached refresh token and makes no interactive
 * redirect unless consent/interaction is required, in which case we fall back to
 * `acquireTokenRedirect`. On a 401 response the active account is cleared and the
 * user is sent to /login.
 */
@Injectable()
export class AuthTokenInterceptor implements HttpInterceptor {
  private readonly msalService = inject(MsalService);

  private readonly apiConfig = inject(ApiConfigService);

  private readonly router = inject(Router);

  private get backendBases(): string[] {
    return [
      this.apiConfig.api,
      this.apiConfig.nav,
      this.apiConfig.docApi,
      this.apiConfig.serviceProviderApi,
      this.apiConfig.collectionApi,
      this.apiConfig.feedbackApi,
      this.apiConfig.notificationApi,
    ];
  }

  private isProtectedUrl(url: string): boolean {
    return isOurBackendUrl(url, this.backendBases, window.location.origin);
  }

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const account = this.msalService.instance.getActiveAccount();

    // Only our own backends get a token, and only when a user is signed in.
    if (!account || !this.isProtectedUrl(req.url)) {
      return next.handle(req)
.pipe(catchError((err: unknown) => this.handleError(err)));
    }

    // Acquire a real ACCESS token for the API scope (silent — no redirect
    // unless interaction is required).
    return from(
      this.msalService.instance.acquireTokenSilent({
        scopes: environment.apiConfig.scopes,
        account,
      })
    )
.pipe(
      mergeMap((result) => {
        const authReq = req.clone({
          setHeaders: { Authorization: `Bearer ${result.accessToken}` },
        });
        return next.handle(authReq);
      }),
      catchError((err: unknown) => this.handleError(err))
    );
  }

  /**
   * Centralised error handling. Silent acquisition that needs interaction kicks
   * off an interactive redirect; a 401 from the API clears the account and
   * bounces the user to /login.
   */
  private handleError(err: unknown): Observable<never> {
    if (err instanceof InteractionRequiredAuthError) {
      // User must (re)consent or re-authenticate — restart auth interactively.
      this.msalService.instance.acquireTokenRedirect({
        scopes: environment.apiConfig.scopes,
      });
      return EMPTY; // navigating away; cancel the in-flight request
    }
    if (err instanceof HttpErrorResponse && err.status === 401) {
      this.msalService.instance.setActiveAccount(null);
      this.router.navigate(["/login"]);
    }
    return throwError(() => err);
  }
}
